import { TranslatedString, useAppConfig } from "@ct-react/core";
import { defaultLocale, useLocaleContext, useLocaleUrl } from "@ct-react/locale";
import { allLocales } from "@shared/locale";
import { useLocation } from "react-router";
import { Head } from "./head";

type Props = {
  title?: string | TranslatedString;
  description?: string | TranslatedString;
  image?: string;
  keywords?: string[];
  canonical?: string;
}

const canonicalQueryParams = new Set([ "refId" ]);

const canonicalSearch = (search: string): string => {
  const source = new URLSearchParams(search);
  const canonical = new URLSearchParams();

  source.forEach((value, key) => {
    if (canonicalQueryParams.has(key)) canonical.append(key, value);
  });

  const query = canonical.toString();
  return query ? `?${query}` : "";
};

const withoutLocalePrefix = (pathname: string, localeBasename: string): string => {
  const prefix = `/${localeBasename}`;

  if (pathname === prefix) return "/";
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length) || "/";
  return pathname || "/";
};

const SeoMetas = (props: Props) => {

  const { locale } = useLocaleContext();
  const { pathname, search } = useLocation();
  const { canonicalBaseUrl: baseUrl } = useAppConfig();
  const { buildLocaleHref } = useLocaleUrl(baseUrl);

  const title = typeof props.title === "string" ? props.title : props.title?.value;
  const description = typeof props.description === "string" ? props.description : props.description?.value;
  const currentPath = withoutLocalePrefix(pathname, locale.basename);
  const canonicalPath = props.canonical ?? `${currentPath}${canonicalSearch(search)}`;

  return (
    <Head>
      {!!title &&
        <>
          <title>{title}</title>
          <meta property="og:title" content={title} />
        </>
      }
      {!!description &&
        <>
          <meta name="description" content={description} />
          <meta property="og:description" content={description} />
        </>
      }
      {!!props.image &&
        <meta property="og:image" content={props.image} />
      }
      {!!(props.keywords && props.keywords.length > 0) &&
        <meta name="keywords" content={props.keywords.join(", ")} />
      }
      <link rel="canonical" href={buildLocaleHref(canonicalPath, locale)} />
      {allLocales.map(loc => (
        <link key={loc.code}
              rel="alternate"
              hrefLang={loc.code}
              href={buildLocaleHref(canonicalPath, loc)} />)
      )}
      <link rel="alternate"
            hrefLang="x-default"
            href={buildLocaleHref(canonicalPath, defaultLocale)} />
    </Head>);
}

export default SeoMetas;
